import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBug } from 'react-icons/fa';
import { testBtnOutline, testFocusRing } from '../test/testUiClasses';
import apiClient from '../../utils/apiClient';

/**
 * Helper button that opens a compact bug-report popup and POSTs to /api/bug-reports.
 */
function BugReportHelperButton({ page = 'jupas-calculator', className = '' }) {
  const { t } = useTranslation();
  const dialogTitleId = useId();
  const textareaRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const timer = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(timer);
    };
  }, [open]);

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) {
      setError(t('bugReport.messageRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await apiClient.post('/api/bug-reports', { message: trimmed, page });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || t('bugReport.submitFailed'));
      }
      setSuccess(true);
      setMessage('');
      window.setTimeout(() => {
        setOpen(false);
        setSuccess(false);
      }, 1200);
    } catch (err) {
      setError(err?.message || t('bugReport.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${testBtnOutline} ${testFocusRing} inline-flex items-center gap-2 ${className}`.trim()}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <FaBug className="h-3.5 w-3.5" aria-hidden />
        <span>{t('bugReport.button')}</span>
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            className="w-full max-w-md rounded-xl border border-edge bg-surface p-5 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id={dialogTitleId} className="text-lg font-semibold text-ink">
                  {t('bugReport.title')}
                </h2>
                <p className="mt-1 text-sm text-ink-muted">{t('bugReport.subtitle')}</p>
              </div>
              <button
                type="button"
                onClick={close}
                className="rounded-md px-2 py-1 text-sm text-ink-subtle hover:bg-surface-muted hover:text-ink"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>

            {success ? (
              <p className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800 dark:bg-green-950/40 dark:text-green-200">
                {t('bugReport.submitSuccess')}
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="mt-4 space-y-3">
                <label className="block">
                  <span className="sr-only">{t('bugReport.messageLabel')}</span>
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder={t('bugReport.messagePlaceholder')}
                    className="w-full resize-y rounded-lg border border-edge-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </label>
                {error ? <p className="text-sm text-red-600">{error}</p> : null}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-lg border border-edge px-3 py-2 text-sm font-medium text-ink-muted hover:bg-surface-muted"
                    disabled={submitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-60"
                  >
                    {submitting ? t('common.loading') : t('bugReport.submit')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default BugReportHelperButton;
